import ExcelJS from "exceljs";

function normalizeCellValue(value) {
  if (value == null || value === "") return "";
  if (value instanceof Date) return value;
  if (typeof value === "object") {
    if (Array.isArray(value.richText)) {
      return value.richText.map((t) => t.text).join("");
    }
    if (value.text != null) return value.text;
    if (value.result !== undefined) return value.result;
    if (value.hyperlink) return value.text ?? value.hyperlink;
  }
  return value;
}

function toArrayBuffer(input) {
  if (input instanceof ArrayBuffer) return input;
  if (input instanceof Uint8Array) {
    return input.buffer.slice(input.byteOffset, input.byteOffset + input.byteLength);
  }
  if (typeof input === "string") {
    const len = input.length;
    const u8 = new Uint8Array(len);
    for (let i = 0; i < len; i++) u8[i] = input.charCodeAt(i) & 0xff;
    return u8.buffer;
  }
  throw new Error("Unsupported workbook input");
}

/** Read first worksheet as array of row objects (header row = keys). Accepts binary string or ArrayBuffer. */
export async function readFirstSheetAsJson(input) {
  const buffer = toArrayBuffer(input);
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(buffer);
  const ws = workbook.worksheets[0];
  if (!ws) return [];

  const rows = [];
  ws.eachRow((row) => {
    const vals = [];
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      vals[colNumber - 1] = normalizeCellValue(cell.value);
    });
    rows.push(vals);
  });

  if (rows.length === 0) return [];
  const headers = rows[0].map((h) => (h == null || h === "" ? "" : String(h)));
  return rows.slice(1).map((cells) => {
    const obj = {};
    headers.forEach((h, i) => {
      if (h !== "") obj[h] = cells[i] !== undefined ? cells[i] : "";
    });
    return obj;
  });
}

function rowsToCsv(rows) {
  if (!rows.length) return "";
  const keys = Object.keys(rows[0]);
  const esc = (v) => {
    const s = v == null ? "" : String(v);
    if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\r\n");
}

export function triggerDownload(blob, fileName) {
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = u;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(u);
}

export async function jsonToXlsxBlob(rows, sheetName = "Sheet1") {
  const workbook = new ExcelJS.Workbook();
  const ws = workbook.addWorksheet(sheetName);
  if (rows && rows.length > 0) {
    const keys = Object.keys(rows[0]);
    ws.addRow(keys);
    rows.forEach((r) => ws.addRow(keys.map((k) => (r[k] == null ? "" : r[k]))));
  }
  const buf = await workbook.xlsx.writeBuffer();
  return new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
}

/** csv uses text/csv; xlsx/xls both download as OOXML .xlsx (legacy BIFF .xls is not produced). */
export async function downloadSpreadsheet(rows, sheetName, fileNameWithoutExt, format) {
  const fmt = (format || "xlsx").toLowerCase();
  if (fmt === "csv") {
    triggerDownload(
      new Blob([rowsToCsv(rows)], { type: "text/csv;charset=utf-8;" }),
      `${fileNameWithoutExt}.csv`
    );
    return;
  }
  const blob = await jsonToXlsxBlob(rows, sheetName);
  triggerDownload(blob, `${fileNameWithoutExt}.xlsx`);
}
